import { WritableAtom, atom, useAtom } from "jotai";
import { Workbook, View } from "../core/Workbook";
import { workbookAtom } from "./WorkbookAtoms";
import { useMemo } from "react";
import { SpreadsheetViewConfig } from "datasheets/views/spreadsheet/SpreadsheetViewConfig";
import { inDraftModeAtom } from "./WorkbookActionAtoms";

export const workbookVersionAtom = atom<string>(
  (get) => get(workbookAtom).version
);

export const workbookNameAtom = atom(
  (get) => {
    const workbook = get(workbookAtom);
    return workbook.name;
  },
  (get, set, data) => {
    const workbook: Workbook = get(workbookAtom);
    const name: string = String(data);
    const newWorkbook: Workbook = { ...workbook, ...{ name: name } };
    set(inDraftModeAtom, false);
    set(workbookAtom, newWorkbook);
  }
);

export const workbookOperationalAtom = atom(
  (get) => {
    const coreObject = get(workbookAtom);
    return coreObject.operational;
  },
  (get, set, arg) => {
    const coreObject = get(workbookAtom);
    const prevFocusValue = coreObject.operational;
    const newFocusValue = typeof arg === "function" ? arg(prevFocusValue) : arg;
    const newObject: Workbook = {
      ...coreObject,
      ...{ operational: newFocusValue },
    };
    set(workbookAtom, newObject);
  }
);

export const selectedViewAtom = atom(
  (get) => {
    const coreObject = get(workbookOperationalAtom);
    return coreObject.selectedView;
  },
  (get, set, arg) => {
    const coreObject = get(workbookOperationalAtom);
    const prevFocusValue = coreObject.selectedView;
    const newFocusValue = typeof arg === "function" ? arg(prevFocusValue) : arg;
    const newObject = {
      ...coreObject,
      ...{ selectedView: newFocusValue },
    };
    set(workbookOperationalAtom, newObject);
  }
);

export const nextSheetNumberAtom = atom(
  (get) => {
    const coreObject = get(workbookOperationalAtom);
    return coreObject.nextSheetNumber;
  },
  (get, set, arg) => {
    const coreObject = get(workbookOperationalAtom);
    const prevFocusValue = coreObject.nextSheetNumber;
    const newFocusValue = typeof arg === "function" ? arg(prevFocusValue) : arg;
    const newObject = { ...coreObject, ...{ nextSheetNumber: newFocusValue } };
    set(workbookOperationalAtom, newObject);
  }
);

export const workbookPersistentAtom = atom(
  (get) => {
    const coreObject: Workbook = get(workbookAtom);
    return coreObject.data;
  },
  (get, set, arg) => {
    const coreObject = get(workbookAtom);
    const prevFocusValue = coreObject.data;
    const newFocusValue = typeof arg === "function" ? arg(prevFocusValue) : arg;
    const newObject: Workbook = {
      ...coreObject,
      ...{ data: newFocusValue },
    };
    set(workbookAtom, newObject);
  }
);

export const datasetsAtom = atom(
  (get) => {
    const coreObject = get(workbookPersistentAtom);
    return coreObject.datasets;
  },
  (get, set, arg) => {
    const coreObject = get(workbookPersistentAtom);
    const prevFocusValue = coreObject.datasets;
    const newFocusValue = typeof arg === "function" ? arg(prevFocusValue) : arg;
    const newObject = {
      ...coreObject,
      ...{ datasets: newFocusValue },
    };
    set(workbookPersistentAtom, newObject);
  }
);

export const viewOrderAtom = atom(
  (get) => {
    const coreObject = get(workbookPersistentAtom);
    return coreObject.viewOrder;
  },
  (get, set, arg) => {
    const coreObject = get(workbookPersistentAtom);
    const prevFocusValue = coreObject.viewOrder;
    const newFocusValue = typeof arg === "function" ? arg(prevFocusValue) : arg;
    const newObject = {
      ...coreObject,
      ...{ viewOrder: newFocusValue },
    };
    set(workbookPersistentAtom, newObject);
  }
);

export const viewsAtom = atom(
  (get) => {
    const coreObject = get(workbookPersistentAtom);
    return coreObject.views;
  },
  (get, set, arg) => {
    const coreObject = get(workbookPersistentAtom);
    const prevFocusValue = coreObject.views;
    const newFocusValue = typeof arg === "function" ? arg(prevFocusValue) : arg;
    const newObject = {
      ...coreObject,
      ...{ views: newFocusValue },
    };
    set(workbookPersistentAtom, newObject);
  }
);

export const viewAtom = atom(
  (get) => {
    const views = get(viewsAtom);
    const selectedViewIdx = get(selectedViewAtom);
    return views[selectedViewIdx];
  },
  (get, set, arg) => {
    const views = get(viewsAtom);
    const selectedViewIdx = get(selectedViewAtom);
    const prevView = views[selectedViewIdx];
    let view;
    if (typeof arg === "function") {
      view = arg(prevView);
    } else {
      view = arg;
    }
    if (view.id !== prevView.id) {
      throw new Error("Trying to update current view with out of date data");
    }
    const newViews = {
      ...views,
      ...{ [prevView.id]: view },
    };
    set(viewsAtom, newViews);
  }
);

export const currentViewConfigAtom = atom(
  (get) => {
    const coreObject = get(viewAtom);
    return coreObject?.config;
  },
  (get, set, arg) => {
    const coreObject = get(viewAtom);
    const prevFocusValue = coreObject.config;
    const newFocusValue = typeof arg === "function" ? arg(prevFocusValue) : arg;
    const newObject = {
      ...coreObject,
      ...{ config: newFocusValue },
    };
    set(viewAtom, newObject);
  }
);

export const currentViewTypeAtom = atom((get) => {
  const coreObject = get(currentViewConfigAtom);
  return coreObject?.type;
});

export const viewForKeyAtom = (key: string) =>
  atom(
    (get) => {
      const views = get(viewsAtom);
      return views[key];
    },
    (get, set, arg) => {
      const views = get(viewsAtom);
      const prevView = views[key];
      let view;
      if (typeof arg === "function") {
        view = arg(prevView);
      } else {
        view = arg;
      }
      const newViews = {
        ...views,
        ...{ [prevView.id]: view },
      };
      set(viewsAtom, newViews);
    }
  );

const nameForViewAtom = (
  viewAtom: WritableAtom<View, View | ((prev: View) => View)>
) =>
  atom<string, string | ((prev: string) => string)>(
    (get) => {
      const view = get(viewAtom);
      return view.name;
    },
    (get, set, arg) => {
      const view = get(viewAtom);
      const prevName = view.name;
      let name;
      if (typeof arg === "function") {
        name = arg(prevName);
      } else {
        name = arg;
      }
      const newView = {
        ...view,
        ...{ name: name },
      };
      set(viewAtom, newView);
      // And, if this is a spreadsheet view, also set the data name
      if (view.config.type === "spreadsheet") {
        const config = view.config as SpreadsheetViewConfig;
        const datasets = get(datasetsAtom);
        const dataset = datasets[config.datasetId];
        const newDataset = { ...dataset };
        newDataset.name = name;
        set(datasetsAtom, {
          ...datasets,
          ...{ [config.datasetId]: newDataset },
        });
      }
    }
  );

export const configForViewAtom = (
  viewAtom: WritableAtom<View, View | ((prev: View) => View)>
) =>
  atom(
    (get) => get(viewAtom).config,
    (get, set, arg) => {
      const prevView = get(viewAtom);
      const prevViewConfig = prevView.config;
      const newViewConfig =
        typeof arg === "function" ? arg(prevViewConfig) : arg;
      set(viewAtom, { ...prevView, ...{ config: newViewConfig } });
    }
  );

export const datasetForKeyAtom = (key: string) =>
  atom(
    (get) => {
      const datasets = get(datasetsAtom);
      return datasets[key];
    },
    (get, set, arg) => {
      const datasets = get(datasetsAtom);
      const prevDataset = datasets[key];
      let dataset;
      if (typeof arg === "function") {
        dataset = arg(prevDataset);
      } else {
        dataset = arg;
      }
      const newDatasets = {
        ...datasets,
        ...{ [prevDataset.id]: dataset },
      };
      set(datasetsAtom, newDatasets);
    }
  );

export const useViewForKeyAtom = (key: string) =>
  useAtom(useMemo(() => viewForKeyAtom(key), [key]));

export const useViewConfigForKeyAtom = (key: string) =>
  useAtom(useMemo(() => configForViewAtom(viewForKeyAtom(key)), [key]));

export const useViewNameForKeyAtom = (key: string) =>
  useAtom(useMemo(() => nameForViewAtom(viewForKeyAtom(key)), [key]));

export const useDatasetForKeyAtom = (key: string) =>
  useAtom(useMemo(() => datasetForKeyAtom(key), [key]));
